import { useState, useEffect, useMemo } from "react";
import {
  API_BASE_URL,
  API_PUBLIC_AUTH_TOKEN,
  API_ENDPOINTS,
} from "../constants/api";
import {
  MaintenanceConfigResponse,
  MaintenanceState,
} from "../types/maintenance-page";
import state from "../maintenance-page/state.json";
import { IS_LOCALHOST } from "../constants/environment";
import { ROUTES } from "../configs/routes";
import { useHistory } from "react-router-dom";

// Cache the maintenance state to avoid repeated fetches
let cachedMaintenanceState: MaintenanceState | null = null;

// Track if maintenance config has been loaded
let maintenanceConfigLoaded = false;

// Export the cached maintenance state for direct access in other modules
export const getCachedMaintenanceState = () => cachedMaintenanceState;

// Export whether maintenance config has been loaded
export const isMaintenanceConfigLoaded = () => maintenanceConfigLoaded;

export const useMaintenance = () => {
  const history = useHistory();
  const [maintenanceState, setMaintenanceState] = useState<MaintenanceState>(
    cachedMaintenanceState || (state as MaintenanceState)
  );
  const [isLoading, setIsLoading] = useState(!cachedMaintenanceState);

  const fetchMaintenanceConfig = async () => {
    try {
      // On localhost, use the local state.json directly without making HTTP requests
      if (IS_LOCALHOST) {
        console.log(
          "Running on localhost, using local state.json for maintenance config"
        );
        cachedMaintenanceState = state as MaintenanceState;
        setMaintenanceState(state as MaintenanceState);
        maintenanceConfigLoaded = true;
        setIsLoading(false);
        return;
      }

      const url = `${API_BASE_URL}${API_ENDPOINTS.MAINTENANCE_CONFIG}`;
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      if (API_PUBLIC_AUTH_TOKEN) {
        headers.Authorization = API_PUBLIC_AUTH_TOKEN;
      }

      const response = await fetch(url, {
        method: "GET",
        headers,
      });

      const data: MaintenanceConfigResponse = await response.json();

      if (data && typeof data.enabled === "boolean") {
        const newState: MaintenanceState = {
          enabled: data.enabled,
          dateTime: data.dateTime,
        };
        cachedMaintenanceState = newState;
        setMaintenanceState(newState);
        maintenanceConfigLoaded = true;
      } else {
        // On error, fall back to local state.json
        console.error(
          "Failed to fetch maintenance config, using local fallback"
        );
        cachedMaintenanceState = state as MaintenanceState;
        maintenanceConfigLoaded = true;
      }
    } catch (error) {
      console.error("Error fetching maintenance config:", error);
      // On error, fall back to local state.json (already set as default)
      cachedMaintenanceState = state as MaintenanceState;
      maintenanceConfigLoaded = true;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // If we already have cached state, don't fetch again
    if (cachedMaintenanceState) {
      setIsLoading(false);
      maintenanceConfigLoaded = true;
      return;
    }

    fetchMaintenanceConfig();
  }, []);

  useEffect(() => {
    if (maintenanceState.enabled) {
      history.replace(ROUTES.ERROR_MAINTENANCE);
    }
  }, [maintenanceState.enabled]);

  return useMemo(
    () => ({
      maintenanceState,
      isLoading,
    }),
    [maintenanceState, isLoading]
  );
};
